import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, Typography, useTheme, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { MANUAL_TRACKING_OR_NO_TRACKING } from '@/shared/hooks/useServerState';
import { AutoIsoObjectType } from '@/features/autoIso/types';
import CameraDownloadIcon, { DownloadButtonState } from './CameraDownloadIcon';
import { usePTZStore } from '@/features/camera/store/usePTZStore';
import Tracking from '@/shared/services/tracking';
import FramingPresetMenu from '@/features/framingPresets/components/FramingPresetMenu';
import { useFramingPresetsQuery } from '@/features/framingPresets/hooks/useFramingPresets';
import { TargetSession, framingTarget } from '@/features/framingPresets/store/framingTargetStore';
import { FramingPreset } from '@/features/framingPresets/types';

interface Camera {
  id: number;
  displayName: string;
}

interface FixedCameraControlsProps {
  clip?: {
    id: number;
    cameraId: number;
    status?: string;
    pan?: number;
    tilt?: number;
    zoom?: number;
    framingPresetId?: number | null;
  };
  availableCameras: Camera[];
  currentSelectedCameraId?: number;
  isSaving?: boolean;
  /** Icon state resolved by the clip group (transient check / X included). */
  downloadState: DownloadButtonState;
  /**
   * `framingPresetId` when the position comes from a preset; the API aims it.
   * Resolves whether the position was saved.
   */
  onSetPosition?: (
    ptz: { pan: number; tilt: number; zoom: number },
    framingPresetId?: number,
  ) => Promise<boolean>;
  onDownloadClick?: () => void;
  onExport?: () => void;
}

export const FixedCameraControls: React.FC<FixedCameraControlsProps> = ({
  clip,
  availableCameras,
  currentSelectedCameraId,
  isSaving,
  downloadState,
  onSetPosition,
  onDownloadClick,
  onExport,
}) => {
  const { getCurrentPTZ } = usePTZStore();
  // Fixed-frame presets (C360-3321): Set / Update Position opens the preset
  // menu, which can open target mode (the section under the player that aims
  // the camera at a spot) for this clip.
  const { fixedFramePresets } = useFlags();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const presetsQuery = useFramingPresetsQuery(
    fixedFramePresets ? currentSelectedCameraId : undefined,
  );
  const { selectedObjectType, trackEntity } = useServerStateContext();
  // Identifies this clip's target mode; closing the clip ends it.
  const owner = useRef({}).current;
  useEffect(() => () => framingTarget.endFor(owner), [owner]);
  // So does turning the flag off: the rink stops aiming the camera.
  useEffect(() => {
    if (!fixedFramePresets) framingTarget.endFor(owner);
  }, [fixedFramePresets, owner]);

  const theme = useTheme();

  const iconState: DownloadButtonState = isSaving ? 'generating' : downloadState;

  const handleCameraButtonClick = () => {
    // If clip is ready, download it. `Download Clip` is emitted by
    // useClipDownloadMutation, which this path reaches via
    // timemachineActions.downloadClip — emitting here too would double-count.
    if (clip && (iconState === 'ready' || iconState === 'download') && onDownloadClick) {
      onDownloadClick();
      return;
    }

    // Otherwise, trigger export
    if (clip && onExport) {
      onExport();
    }
  };

  const readCurrentPTZ = () => {
    // Capture current PTZ position from the store
    const ptz = getCurrentPTZ ? getCurrentPTZ() : { pan: -1, tilt: -1, zoom: -1 };
    if (ptz.pan === -1 && ptz.tilt === -1 && ptz.zoom === -1) {
      throw new Error('PTZ values are not set for fixed camera mode.');
    }
    return ptz;
  };

  const onCapturePosition = () => {
    const ptz = readCurrentPTZ();
    if (onSetPosition) {
      Tracking.getInstance().track('Set Clip Position', {
        category: 'Clips',
        action: clip ? 'Update Position' : 'Set Position',
      });
      onSetPosition(ptz);
    }
  };

  // Target mode outlives renders, so it reaches this clip through refs.
  const latestRef = useRef({ readCurrentPTZ, onSetPosition, hasClip: !!clip });
  latestRef.current = { readCurrentPTZ, onSetPosition, hasClip: !!clip };

  // Target mode holds the camera on a spot, so it leaves Auto-ISO.
  const beginTargetMode = () => {
    if (selectedObjectType !== AutoIsoObjectType.None) {
      trackEntity(MANUAL_TRACKING_OR_NO_TRACKING, 'Framing Preset');
    }
    const session: TargetSession = {
      owner,
      readCurrentPTZ: () => latestRef.current.readCurrentPTZ(),
      apply: (ptz, framingPresetId) => {
        Tracking.getInstance().track('Set Clip Position', {
          category: 'Clips',
          action: latestRef.current.hasClip ? 'Update Position' : 'Set Position',
          source: framingPresetId ? 'Framing Preset' : 'Target',
        });
        return latestRef.current.onSetPosition?.(ptz, framingPresetId) ?? Promise.resolve(false);
      },
    };
    framingTarget.begin(session);
    return session;
  };

  // Applies in one click, then shows the spot in target mode with Done once
  // saved. A failed save leaves it on the spot to try again.
  const applyPreset = (preset: FramingPreset) => {
    const { zoom } = readCurrentPTZ();
    const session = beginTargetMode();
    framingTarget.loadPreset(preset);
    const spot = framingTarget.get().target;
    framingTarget.applyTo(session, { ...preset.aim, zoom }, preset.id).then((saved) => {
      if (saved && framingTarget.isCurrent(session, spot)) framingTarget.markApplied(preset.name);
    });
  };

  // Get PTZ from persisted clip
  const displayedPTZ =
    clip?.pan !== undefined && clip?.tilt !== undefined && clip?.zoom !== undefined
      ? { pan: clip.pan, tilt: clip.tilt, zoom: clip.zoom }
      : null;

  // Show "Update Position" if there's a persisted clip
  const hasExistingPosition = !!clip;

  // Get camera name from the clip (or current selection as preview)
  const displayedCameraId = clip?.cameraId || currentSelectedCameraId;
  const displayedCamera = availableCameras.find((c) => c.id === displayedCameraId);
  const displayedCameraName = displayedCamera?.displayName || 'Unknown Camera';

  // For tooltip
  const previewCamera = availableCameras.find((c) => c.id === currentSelectedCameraId);
  const previewCameraName = previewCamera?.displayName || 'current camera';
  const tooltipText = fixedFramePresets
    ? `Pick a saved preset, aim at a spot on the rink, or use the ${previewCameraName} frame in preview`
    : hasExistingPosition
      ? `Click to update with ${previewCameraName} position currently in preview`
      : `Sets to ${previewCameraName} position currently in preview`;

  const activePreset =
    fixedFramePresets && clip?.framingPresetId
      ? presetsQuery.data?.find((p) => p.id === clip.framingPresetId)
      : undefined;
  const positionText = displayedPTZ
    ? `${displayedCameraName}, ${displayedPTZ.pan.toFixed(2)}, ${displayedPTZ.tilt.toFixed(2)}, ${displayedPTZ.zoom.toFixed(2)}`
    : '';

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 1.5,
        p: 1,
        minHeight: 44,
        borderRadius: 1,
        backgroundColor: theme.palette.cx.bg.surface2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
        <Tooltip title={tooltipText} placement="top">
          <Button
            onClick={fixedFramePresets ? (e) => setMenuAnchor(e.currentTarget) : onCapturePosition}
            aria-haspopup={fixedFramePresets ? 'menu' : undefined}
            aria-expanded={fixedFramePresets ? !!menuAnchor : undefined}
            sx={{
              border: `1px solid ${theme.palette.cx.text.primary}`,
              color: theme.palette.cx.text.primary,
              backgroundColor: 'transparent',
              borderRadius: 1,
              minWidth: 0,
              // Never wrap next to a long position label.
              flexShrink: 0,
              whiteSpace: 'nowrap',
              height: 32,
              px: 1,
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '16px',
              textTransform: 'none',
            }}
          >
            {hasExistingPosition ? 'Update Position' : 'Set Position'}
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 14,
                height: 14,
                ml: 0.75,
                border: `1px solid ${theme.palette.cx.text.tertiary}`,
                borderRadius: '50%',
                color: theme.palette.cx.text.tertiary,
                fontSize: '9px',
                lineHeight: 1,
              }}
            >
              i
            </Box>
          </Button>
        </Tooltip>
        {displayedPTZ && activePreset && (
          <Tooltip title={positionText} placement="top">
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: '12px', lineHeight: '16px', color: theme.palette.cx.text.primary }}
              >
                {activePreset.name}
              </Typography>
              <Typography
                noWrap
                sx={{ fontSize: '11px', lineHeight: '14px', color: theme.palette.cx.text.tertiary }}
              >
                {positionText}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {displayedPTZ && !activePreset && (
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.cx.text.secondary,
              lineHeight: '16px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {positionText}
          </Typography>
        )}
      </Box>
      {fixedFramePresets && onSetPosition && (
        <FramingPresetMenu
          anchorEl={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          cameraId={currentSelectedCameraId}
          activePresetId={clip?.framingPresetId}
          busy={!!isSaving}
          onApplyPreset={applyPreset}
          onAimAtSpot={() => {
            Tracking.getInstance().track('Aim At Spot', { category: 'Clips' });
            beginTargetMode();
          }}
          onUseCurrent={() => {
            framingTarget.endFor(owner);
            onCapturePosition();
          }}
        />
      )}
      <CameraDownloadIcon
        state={iconState}
        onClick={handleCameraButtonClick}
        size="medium"
        disabled={!clip}
      />
    </Stack>
  );
};

export default FixedCameraControls;
